
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LegacyJob, LegacyCity, DEPARTMENT_KEY_MAP, JOB_TYPE_KEY_MAP } from '../types';
import { useCities } from '../src/contexts/CitiesContext';
import { formatRelativeTime } from '../src/utils/dateFormatter';
import ApplicationForm from './ApplicationForm';
import ApplicationSuccess from './ApplicationSuccess';
import { showToast } from './Toast';
import {
  trackJobShared,
  trackApplyClicked,
  trackJobCityClicked,
  trackApplicationSuccessViewed,
  trackApplicationAbandoned,
} from '../src/lib/analytics';

// 使用 Legacy 类型用于向后兼容
type Job = LegacyJob;
type City = LegacyCity;

interface JobDetailProps {
  job: Job;
  onBack: () => void;
  // 添加上下文信息用于显示面包屑
  sourceView?: 'home' | 'all-jobs' | 'offices' | 'life';
  selectedCity?: City | null;
  onCityClick?: () => void; // 新增：点击城市时的回调
}

const JobDetail: React.FC<JobDetailProps> = ({ job, onBack, sourceView = 'home', selectedCity, onCityClick }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const { citiesMap } = useCities();

  // 根据 job.city 查找对应的城市信息
  const jobCity = job.city ? citiesMap.get(job.city) || null : null;

  // 使用映射表获取部门翻译
  const getDepartmentTranslation = (dept: string) => {
    const key = DEPARTMENT_KEY_MAP[dept];
    if (!key) return dept;
    return t(`jobData.departments.${key}`, dept);
  };

  // 使用映射表获取职位类型翻译
  const getJobTypeTranslation = (type: string) => {
    const key = JOB_TYPE_KEY_MAP[type as keyof typeof JOB_TYPE_KEY_MAP];
    if (!key) return type;
    return t(`jobData.jobTypes.${key}`, type);
  };

  // 获取工作模式翻译
  const getWorkStyleTranslation = (workStyle: string) => {
    const key = workStyle === 'HOME_BASED' ? 'homeBased' :
                workStyle === 'REMOTE' ? 'remote' : 
                workStyle === 'HYBRID' ? 'hybrid' : 'onsite';
    return t(`jobData.workStyles.${key}`);
  };
  // 判断工作模式类型
  const isRemoteWork = (workStyle: string) => {
    return workStyle === 'REMOTE';
  };
  const isHomeBasedWork = (workStyle: string) => {
    return workStyle === 'HOME_BASED';
  };
  const isHybridWork = (workStyle: string) => {
    return workStyle === 'HYBRID';
  };

  // 获取城市名称（根据语言，兼容旧格式和新 API 格式）
  const getCityName = (city: { name?: string; enName?: string; nameZh?: string; nameEn?: string }) => {
    const zh = city.nameZh || city.name || '';
    const en = city.nameEn || city.enName || '';
    return currentLang.startsWith('zh') ? zh : en;
  };

  // 获取国家翻译
  const getCountryTranslation = (country: string) => {
    const countryMap: Record<string, string> = {
      'China': 'china',
      'UK': 'uk',
      'USA': 'usa',
      'India': 'india',
      'Vietnam': 'vietnam',
      'Malaysia': 'malaysia',
      'Singapore': 'singapore',
      'Thailand': 'thailand',
      'Indonesia': 'indonesia',
      'South Korea': 'southKorea'
    };
    const key = countryMap[country];
    if (!key) return country;
    return t(`jobData.countries.${key}`, country);
  };

  // 根据 cities 数组生成位置显示（优先使用结构化数据）
  const formatLocationFromCities = (cities: string[] | null | undefined, country: string | null | undefined) => {
    if (!cities || cities.length === 0) {
      return null; // 返回 null 表示需要 fallback 到 location 字段
    }

    const cityNames = cities.map(cityId => {
      const cityData = citiesMap.get(cityId);
      return cityData
        ? (currentLang.startsWith('zh') ? cityData.nameZh : cityData.nameEn)
        : cityId;
    }).join('/');

    if (country) {
      return `${cityNames}, ${getCountryTranslation(country)}`;
    }
    return cityNames;
  };

  // 格式化位置信息（将城市名和国家名翻译）
  const formatLocation = (location: string) => {
    // 处理特殊的全球远程位置
    if (location === 'Remote (Global)') {
      return t('jobData.locations.remoteGlobal', location);
    }

    // 处理 "City/Remote" 格式（如 "Tianjin/Remote"）
    if (location.includes('/Remote')) {
      const cityPart = location.replace('/Remote', '');
      const cityData = [...citiesMap.values()].find(c => cityPart === c.nameZh || cityPart === c.nameEn);
      const cityName = cityData
        ? (currentLang.startsWith('zh') ? cityData.nameZh : cityData.nameEn)
        : cityPart;
      const remoteText = t('jobData.locations.remote', 'Remote');
      return `${cityName}/${remoteText}`;
    }

    // 分割国家和城市部分
    const parts = location.split(', ');
    let countryName = '';

    // 如果有国家部分
    if (parts.length >= 2) {
      countryName = getCountryTranslation(parts[parts.length - 1]);
    }

    // 处理城市部分（可能包含多个城市用 / 分隔）
    const cityPart = parts[0] || location;
    const cityNames = cityPart.split('/').map(city => {
      const trimmedCity = city.trim();
      const cityData = [...citiesMap.values()].find(c => trimmedCity === c.nameZh || trimmedCity === c.nameEn);
      return cityData
        ? (currentLang.startsWith('zh') ? cityData.nameZh : cityData.nameEn)
        : trimmedCity;
    }).join('/');

    // 如果有国家，返回 "城市/城市, 国家" 格式
    if (parts.length >= 2) {
      return `${cityNames}, ${countryName}`;
    }

    // 处理只有城市名的情况（如 "Singapore"）
    return cityNames;
  };

  // 获取显示位置：优先使用 cities 数组，fallback 到 location 字段
  const getDisplayLocation = () => {
    // 优先使用 cities 数组（结构化数据更可靠）
    const fromCities = formatLocationFromCities(job.cities, job.country);
    if (fromCities) {
      return fromCities;
    }
    // fallback 到 location 字段
    return formatLocation(job.location);
  };

  // 社会证明数据（确保有数值）
  const viewCount = job.viewCount || Math.floor(Math.random() * 400) + 100;
  const applicantCount = job.applicantCount || Math.floor(Math.random() * 30) + 5;

  // 判断是否显示社会证明（count 存在且大于 0）
  const showViewCount = viewCount > 0;
  const showApplicantCount = applicantCount > 0;

  const [view, setView] = useState<'detail' | 'form' | 'success'>('detail');
  const [showStickyBar, setShowStickyBar] = useState(false);

  // 使用 useCallback 优化回调函数，防止不必要的重新渲染
  const handleApplyClick = useCallback(() => {
    trackApplyClicked(job.id, job.title, 'detail_button');
    setView('form');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [job.id, job.title]);

  const handleStickyApplyClick = useCallback(() => {
    trackApplyClicked(job.id, job.title, 'sticky_bar');
    setView('form');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [job.id, job.title]);

  const handleFormSubmit = useCallback(() => {
    trackApplicationSuccessViewed(job.id);
    setView('success');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [job.id]);

  const handleFormBack = useCallback(() => {
    trackApplicationAbandoned(job.id, 0);
    setView('detail');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [job.id]);

  // 分享功能 - 复制链接
  const handleShare = useCallback(async () => {
    trackJobShared(job.id, job.title);
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast(
        t('jobData.common.linkCopied'),
        'success',
        2000
      );
    } catch (err) {
      // 降级方案：使用传统方法
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showToast(
        t('jobData.common.linkCopied'),
        'success',
        2000
      );
    }
  }, [t]);

  useEffect(() => {
    const handleScroll = () => {
      // Show sticky bar when user scrolls past the hero section
      setShowStickyBar(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (view === 'success') {
    return <ApplicationSuccess job={job} onBack={onBack} />;
  }

  if (view === 'form') {
    return <ApplicationForm job={job} onBack={handleFormBack} onSubmit={handleFormSubmit} />;
  }

  return (
    <div className="bg-white min-h-screen">
      {/* Sticky Action Bar */}
      <div className={`fixed top-16 sm:top-16 left-0 right-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-100 transition-all duration-300 ${showStickyBar ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="bg-blue-600 text-white text-[8px] sm:text-[10px] font-black px-1.5 py-0.5 rounded-xl uppercase shrink-0">{getDepartmentTranslation(job.department)}</div>
            <h4 className="font-black text-slate-900 text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[150px] md:max-w-md">{job.title}</h4>
          </div>
          <button
            onClick={handleStickyApplyClick}
            className="bg-slate-900 text-white px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 rounded-xl font-black text-[11px] sm:text-sm hover:bg-blue-600 active:scale-95 focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus-visible:ring-4 transition-all shadow-lg shadow-blue-500/10 disabled:opacity-50 disabled:cursor-not-allowed shrink-0 cursor-pointer"
          >
            {t('jobDetail.applyNow')}
          </button>
        </div>
      </div>

      <div className="pt-20 sm:pt-24 pb-24 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Enhanced Breadcrumb Navigation */}
          <div className="mb-6 sm:mb-8">
            <nav className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
              <Link
                to={sourceView === 'all-jobs' ? '/jobs' : sourceView === 'offices' ? '/offices' : '/'}
                className="flex items-center gap-1 sm:gap-1.5 font-bold text-slate-400 hover:text-blue-600 transition-colors group shrink-0 cursor-pointer"
              >
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden xs:inline">
                  {sourceView === 'all-jobs' 
                    ? t('jobDetail.breadcrumb.allJobs', 'All Jobs') 
                    : sourceView === 'offices' 
                      ? t('jobDetail.breadcrumb.globalOffices', 'Global Offices') 
                      : t('jobDetail.breadcrumb.home', 'Home')}
                </span>
              </Link>
              {selectedCity && (
                <>
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                  <Link
                    to={`/cities/${selectedCity.id}`}
                    className="font-medium text-slate-500 hover:text-blue-600 transition-colors truncate cursor-pointer"
                  >
                    {getCityName(selectedCity)}
                  </Link>
                </>
              )}
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
              <span className="font-bold text-slate-900 truncate">{job.title}</span>
            </nav>
          </div>

          {/* Hero Info */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 sm:gap-6 lg:gap-8 pb-6 sm:pb-8 lg:pb-12 border-b border-slate-100">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3 mb-2 sm:mb-3 lg:mb-4">
                <span className="bg-blue-600 text-white text-[8px] sm:text-[10px] font-black uppercase tracking-[0.15em] sm:tracking-[0.2em] px-1.5 sm:px-2 lg:px-2.5 py-0.5 sm:py-1 rounded-xl">
                  {getDepartmentTranslation(job.department)}
                </span>
                <span className="text-slate-400 font-black text-[8px] sm:text-[10px] lg:text-xs uppercase tracking-wider sm:tracking-widest">
                  {t('jobDetail.postedAt')} {formatRelativeTime(job.postedDate, i18n.language)}
                </span>
              </div>
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-6xl font-black text-slate-900 tracking-tight mb-3 sm:mb-4 lg:mb-8 leading-normal lg:leading-snug">
                {job.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 lg:gap-8 text-slate-600 text-xs sm:text-sm lg:text-base font-medium">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 shrink-0">
                    <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                  </div>
                  <span className="truncate">{getDisplayLocation()}</span>
                </div>

                {/* Work Style Badge */}
                <div className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full ${
                  isRemoteWork(job.workStyle) ? 'bg-purple-50' :
                  isHomeBasedWork(job.workStyle) ? 'bg-green-50' :
                  isHybridWork(job.workStyle) ? 'bg-blue-50' : 'bg-slate-50'
                }`}>
                  <div className={`w-4 h-4 sm:w-5 sm:h-5 lg:w-5 lg:h-5 ${
                    isRemoteWork(job.workStyle) ? 'text-purple-600' :
                    isHomeBasedWork(job.workStyle) ? 'text-green-600' :
                    isHybridWork(job.workStyle) ? 'text-blue-600' : 'text-slate-500'
                  }`}>
                    {isRemoteWork(job.workStyle) ? (
                      <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : isHomeBasedWork(job.workStyle) ? (
                      <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                    ) : isHybridWork(job.workStyle) ? (
                      <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    ) : (
                      <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </div>
                  <span className={`font-black text-[10px] sm:text-xs lg:text-sm ${
                    isRemoteWork(job.workStyle) ? 'text-purple-700' :
                    isHomeBasedWork(job.workStyle) ? 'text-green-700' :
                    isHybridWork(job.workStyle) ? 'text-blue-700' : 'text-slate-600'
                  }`}>
                    {getWorkStyleTranslation(job.workStyle)}
                  </span>
                </div>

                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 shrink-0">
                    <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <span className="truncate">{getJobTypeTranslation(job.type)}</span>
                </div>
              </div>
            </div>
            <div className="shrink-0 flex flex-col items-stretch sm:items-end gap-3 sm:gap-4 lg:gap-6 w-full sm:w-auto">
              {/* 桌面端：分享+申请按钮 */}
              <div className="hidden sm:flex gap-3">
                <button
                  onClick={handleShare}
                  className="bg-white text-slate-600 p-2.5 sm:p-3 lg:p-4 rounded-xl font-black hover:bg-slate-50 hover:text-blue-600 active:scale-95 focus:outline-none focus:ring-4 focus:ring-slate-200 transition-all border border-slate-200 cursor-pointer"
                  title={t('jobData.common.shareJob')}
                >
                  <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </button>
                <button
                  onClick={handleApplyClick}
                  className="bg-slate-900 text-white px-6 lg:px-10 py-3 lg:py-5 rounded-xl font-black text-base lg:text-lg hover:bg-blue-600 hover:shadow-xl active:scale-95 focus:outline-none focus:ring-4 focus:ring-blue-500/30 transition-all shadow-2xl shadow-blue-500/10 cursor-pointer"
                >
                  {t('jobData.common.applyNow')}
                </button>
              </div>

              {/* 社会证明 - 移动端横向一行，桌面端纵向 */}
              <div className="flex flex-wrap sm:flex-col gap-x-4 gap-y-1.5 sm:gap-2.5 lg:gap-3 text-[10px] sm:text-xs lg:text-sm text-slate-500">
                {showViewCount && (
                  <div className="flex items-center gap-1.5">
                    <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 text-green-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold">{t('jobData.common.viewingThisWeek', { count: viewCount })}</span>
                  </div>
                )}
                {showApplicantCount && (
                  <div className="flex items-center gap-1.5">
                    <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 text-blue-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold">{t('jobData.common.applicants', { count: applicantCount })}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 text-orange-500 font-black">
                  <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <span>{t('jobData.common.hourResponse')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Content Body */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-12 lg:gap-16 py-8 sm:py-12 lg:py-16">
            <div className="lg:col-span-2 space-y-8 sm:space-y-12">
              {/* 职位描述 - 统一列表样式 */}
              <section>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6 tracking-tight">{t('jobDetail.sections.description')}</h2>
                <ul className="space-y-3 sm:space-y-4">
                  {job.description?.map((p, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-slate-600 text-base sm:text-lg leading-relaxed">
                      <span className="shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mt-0.5 sm:mt-1">
                        <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="currentColor" viewBox="0 0 20 20">
                          <circle cx="10" cy="10" r="3" />
                        </svg>
                      </span>
                      {p}
                    </li>
                  )) || <li>{t('jobDetail.defaultDescription')}</li>}
                </ul>
              </section>

              {/* 任职要求 - 蓝色勾 */}
              <section>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6 tracking-tight">{t('jobDetail.sections.requirements')}</h2>
                <ul className="space-y-3 sm:space-y-4">
                  {job.requirements?.map((req, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-slate-600 text-base sm:text-lg leading-relaxed">
                      <span className="shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mt-0.5 sm:mt-1">
                        <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                      </span>
                      {req}
                    </li>
                  ))}
                </ul>
              </section>

              {/* 福利待遇 - 绿色加号，统一列表样式 */}
              <section>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6 tracking-tight">{t('jobDetail.sections.benefits')}</h2>
                <ul className="space-y-3 sm:space-y-4">
                  {job.benefits?.map((benefit, i) => {
                    // 兼容字符串数组和对象数组两种格式
                    const benefitText = typeof benefit === 'string' 
                      ? benefit 
                      : (currentLang.startsWith('zh') ? benefit.zh : benefit.en);
                    return (
                      <li key={i} className="flex items-start gap-3 sm:gap-4 text-slate-600 text-base sm:text-lg leading-relaxed">
                        <span className="shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-green-50 text-green-600 flex items-center justify-center mt-0.5 sm:mt-1">
                          <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" /></svg>
                        </span>
                        {benefitText}
                      </li>
                    );
                  })}
                </ul>
              </section>
            </div>

            <aside className="space-y-6 sm:space-y-8">
              <div className="p-4 sm:p-8 rounded-xl bg-slate-900 text-white shadow-2xl shadow-slate-200">
                <h3 className="text-base sm:text-xl font-bold mb-4 sm:mb-8">{t('jobDetail.hiringProcess')}</h3>
                {/* 移动端横向排列，桌面端纵向 */}
                <div className="grid grid-cols-3 gap-3 sm:block sm:space-y-8">
                  {[
                    { step: '01', ...(t('jobDetail.hiringSteps.step1', { returnObjects: true }) as { title: string; desc: string }) },
                    { step: '02', ...(t('jobDetail.hiringSteps.step2', { returnObjects: true }) as { title: string; desc: string }) },
                    { step: '03', ...(t('jobDetail.hiringSteps.step3', { returnObjects: true }) as { title: string; desc: string }) }
                  ].map(item => (
                    <div key={item.step} className="text-center sm:text-left sm:flex sm:gap-4">
                      <div className="text-blue-400 font-black text-lg sm:text-xs mb-1 sm:mb-0 sm:pt-1">{item.step}</div>
                      <div>
                        <div className="font-bold text-xs sm:text-sm mb-0.5 sm:mb-1">{item.title}</div>
                        <div className="text-slate-400 text-[10px] sm:text-xs hidden sm:block">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-6 sm:p-8 rounded-xl border border-slate-100 bg-[#FBFDFF] shadow-sm">
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 sm:mb-6 tracking-tight">{t('jobDetail.sidebar.location')}</h3>
                {(selectedCity || jobCity) ? (
                  <>
                    <Link
                      to={`/cities/${(selectedCity || jobCity)!.id}`}
                      className="relative h-36 sm:h-48 rounded-xl overflow-hidden mb-4 sm:mb-6 cursor-pointer group shadow-sm hover:shadow-lg transition-shadow block"
                    >
                      <img
                        src={(selectedCity || jobCity)!.image}
                        alt={getCityName((selectedCity || jobCity)!)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="bg-white/90 text-slate-900 px-4 py-2 rounded-full text-xs sm:text-sm font-bold shadow-lg">
                          {t('jobDetail.sidebar.viewCityJobs', { city: getCityName((selectedCity || jobCity)!) })}
                        </span>
                      </div>
                    </Link>
                    <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                      {t('jobDetail.sidebar.hubDescription', { city: getCityName((selectedCity || jobCity)!) })}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="relative h-36 sm:h-48 rounded-xl overflow-hidden mb-4 sm:mb-6">
                      <img
                        src="https://images.unsplash.com/photo-1547984609-4b149d478e6d?auto=format&fit=crop&q=80&w=600"
                        alt={formatLocation(job.location)}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/10" />
                    </div>
                    <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                      {t('jobDetail.sidebar.hubDescription', { city: formatLocation(job.location) })}
                    </p>
                  </>
                )}
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* 移动端底部固定操作栏 */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-slate-200 px-4 py-3 safe-area-bottom">
        <div className="flex items-center gap-3">
          <button
            onClick={handleShare}
            className="icon-btn-safe bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 active:scale-95 transition-all cursor-pointer shrink-0"
            title={t('jobData.common.shareJob')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
          </button>
          <button
            onClick={handleApplyClick}
            className="flex-1 btn-safe-touch bg-slate-900 text-white py-4 rounded-xl font-black text-sm hover:bg-blue-600 active:scale-[0.98] transition-all shadow-lg cursor-pointer"
          >
            {t('jobData.common.applyNow')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(JobDetail);
